import { GetEntityTeamRes } from '@/api/entity/types';

/**
 * `GET /roles/:roleId/members` is ID references only — the entity context
 * never resolves name/email (cross-context, owned by `users`). The FE joins
 * display data client-side from the team-associates query the manage-team page
 * already fetches (`_getEntityViewsTeamAssociates`), the same source
 * `AddMembersModal` already uses for its candidate list.
 */
export type ResolvedTeamMember = {
  name: string;
  email: string;
};

export type TeamMemberLookup = ReadonlyMap<string, ResolvedTeamMember>;

export const FALLBACK_MEMBER_NAME = 'Former team member';

export const buildTeamMemberLookup = (team?: GetEntityTeamRes): TeamMemberLookup => {
  const lookup = new Map<string, ResolvedTeamMember>();

  team?.items.forEach((item) => {
    if (!item.userId) return;

    lookup.set(item.userId, { name: item.name ?? item.alias ?? item.email, email: item.email });
  });

  return lookup;
};

/**
 * A role member no longer on the team-associates page the lookup was built
 * from (e.g. beyond its page size, or removed from the team but not yet from
 * the role) still needs something to render — the id itself, rather than a
 * blank row.
 */
export const resolveTeamMember = (lookup: TeamMemberLookup, userId: string): ResolvedTeamMember =>
  lookup.get(userId) ?? { name: FALLBACK_MEMBER_NAME, email: userId };
